import { useState } from 'react';

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookingModal({ isOpen, onClose }: BookingModalProps) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value as string));
    try {
      await fetch('https://readdy.ai/api/form/d727at4991ove783tccg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#151515] border border-amber-500/20 rounded-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/5">
          <div>
            <h3 className="text-white font-black text-xl">施術予約</h3>
            <p className="text-gray-400 text-sm mt-0.5">フィジオショット体験の予約フォーム</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {!submitted ? (
          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="px-6 py-6 space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-gray-300 text-sm font-medium block mb-1.5">お名前 <span className="text-amber-400">*</span></label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="山田 太郎"
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
                />
              </div>
              <div>
                <label className="text-gray-300 text-sm font-medium block mb-1.5">電話番号 <span className="text-amber-400">*</span></label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder="090-0000-0000"
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
                />
              </div>
            </div>

            <div>
              <label className="text-gray-300 text-sm font-medium block mb-1.5">メールアドレス <span className="text-amber-400">*</span></label>
              <input
                type="email"
                name="email"
                required
                placeholder="your@email.com"
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-500/50"
              />
            </div>

            <div>
              <label className="text-gray-300 text-sm font-medium block mb-1.5">お住まいの都道府県 <span className="text-amber-400">*</span></label>
              <select
                name="prefecture"
                required
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-amber-500/50"
              >
                <option value="">選択してください</option>
                {['北海道','青森県','岩手県','宮城県','秋田県','山形県','福島県','茨城県','栃木県','群馬県',
                  '埼玉県','千葉県','東京都','神奈川県','新潟県','富山県','石川県','福井県','山梨県','長野県',
                  '岐阜県','静岡県','愛知県','三重県','滋賀県','京都府','大阪府','兵庫県','奈良県','和歌山県',
                  '鳥取県','島根県','岡山県','広島県','山口県','徳島県','香川県','愛媛県','高知県','福岡県',
                  '佐賀県','長崎県','熊本県','大分県','宮崎県','鹿児島県','沖縄県'].map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-gray-300 text-sm font-medium block mb-1.5">お悩み・ご相談内容</label>
              <textarea
                name="message"
                rows={3}
                maxLength={500}
                placeholder="気になる症状やご要望をお聞かせください（500文字以内）"
                className="w-full bg-[#1A1A1A] border border-white/10 rounded-md px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-amber-500/50 resize-none"
                onChange={(e) => setCharCount(e.target.value.length)}
              />
              <p className="text-gray-600 text-xs text-right mt-1">{charCount}/500</p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-black text-base rounded-md transition-all cursor-pointer whitespace-nowrap"
            >
              {submitting ? '送信中...' : '予約を申し込む'}
            </button>
            <p className="text-gray-500 text-xs text-center">通常24時間以内にご連絡します</p>
          </form>
        ) : (
          <div className="px-6 py-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-500/10 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-amber-400 text-3xl" />
            </div>
            <h4 className="text-white font-black text-xl mb-2">予約申し込みが完了しました！</h4>
            <p className="text-gray-400 text-sm leading-relaxed">
              ご登録いただいたメールアドレスに確認メールをお送りします。
              通常24時間以内にご連絡いたします。
            </p>
            <button onClick={onClose} className="mt-6 px-8 py-3 bg-amber-500 text-black font-bold rounded-md cursor-pointer whitespace-nowrap">
              閉じる
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
